import { faPencil, faTrash, faUpload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DataTable } from "@ventumsoftware/react-toolkit";
import moment from "moment";
import React, { type FC, useState } from "react";
import { useApi } from "../../api/APIContext";
import type { DefaultInspectionAttachment } from "../../api/dto";
import { ContentContainer } from "../../components/content/ContentContainer";
import { PageHeader } from "../../components/content/PageHeader";
import { Badge, BadgeStyle } from "../../components/data/Badge";
import { TablePlaceholder } from "../../components/data/TablePlaceholder";
import { Button } from "../../components/form/Button";
import { Loading } from "../../components/Loading";
import { useModal } from "../../components/layout/ModalProvider";
import { Paragraph } from "../../components/Paragraph";
import { AddDefaultInspectionAttachmentModal } from "../../modals/AddDefaultInspectionAttachmentModal";
import { DeleteDefaultInspectionAttachmentModal } from "../../modals/DeleteDefaultInspectionAttachmentModal";
import { EditDefaultInspectionAttachmentModal } from "../../modals/EditDefaultInspectionAttachmentModal";

export const SettingsAttachmentsPage: FC<{}> = () => {
  const { defaultInspectionAttachments, loading, refresh } = useApi();
  const addModal = useModal({
    title: "Bijlage uploaden",
    body: <AddDefaultInspectionAttachmentModal reload={refresh.defaultInspectionAttachments} />,
    size: "lg",
  });

  return (
    <ContentContainer size={"lg"}>
      <div className={"flex items-center justify-between"}>
        <PageHeader>Inspectie Bijlagen</PageHeader>
        <div>
          <Button
            icon={faUpload}
            type={"primary"}
            size={"md"}
            text={"Bijlage uploaden"}
            onClick={() => addModal.open()}
          />
        </div>
      </div>

      <div className={"-mt-2 max-w-(--breakpoint-lg)"}>
        <Paragraph>
          Beheer hier de bijlagen die automatisch meegestuurd worden bij het exporteren of versturen
          van een Inspectie Rapport.
        </Paragraph>
      </div>

      <Loading loading={loading}>
        {defaultInspectionAttachments.length === 0 ? (
          <TablePlaceholder>Geen bijlagen gevonden</TablePlaceholder>
        ) : (
          <DataTable
            keyProperty={"id"}
            data={defaultInspectionAttachments}
            pageSize={50}
            columns={[
              {
                header: "Naam",
                sort: true,
                property: "name",
                transform: (name, row) => {
                  return (
                    <div className={"flex flex-row gap-4 items-center justify-between"}>
                      <div>{name}</div>
                      {row.isActive && <Badge style={BadgeStyle.info}>Actief</Badge>}
                    </div>
                  );
                },
              },
              {
                header: "Volgorde",
                property: "order",
              },
              {
                header: "Vanaf",
                property: "fromDate",
                transform: (from) => {
                  return <div>{moment(from).format("D-M-Y")}</div>;
                },
              },
              {
                header: "Tot en met",
                property: "toDate",
                transform: (to) => {
                  return <div>{moment(to).format("D-M-Y")}</div>;
                },
              },
              {
                header: "",
                property: "id",
                transform: (_, row) => {
                  return (
                    <AttachmentTableRow
                      attachment={row}
                      reload={refresh.defaultInspectionAttachments}
                    />
                  );
                },
              },
            ]}
          />
        )}
      </Loading>
    </ContentContainer>
  );
};

const AttachmentTableRow: FC<{
  attachment: DefaultInspectionAttachment;
  reload: (silently: boolean) => Promise<void>;
}> = ({ attachment, reload }) => {
  const editModal = useModal({
    title: "Bijlage bewerken",
    body: <EditDefaultInspectionAttachmentModal attachment={attachment} reload={reload} />,
    size: "lg",
  });
  const deleteModal = useModal({
    title: "Bijlage verwijderen",
    body: <DeleteDefaultInspectionAttachmentModal attachment={attachment} reload={reload} />,
    size: "md",
  });
  return (
    <div className={"flex flex-row gap-4 justify-end items-center"}>
      <div
        className={"px-1 py-0.5 text-brand-800 hover:bg-brand-100 rounded-md"}
        onClick={() => editModal.open()}
      >
        <FontAwesomeIcon icon={faPencil} />
      </div>
      <div
        className={"px-1 py-0.5 text-red-800 hover:bg-red-100 rounded-md"}
        onClick={() => deleteModal.open()}
      >
        <FontAwesomeIcon icon={faTrash} />
      </div>
    </div>
  );
};
